/**
 * Unscheduled edge tab: the folder tab that pulls the Unscheduled panel out.
 *
 * The tab sits on the edge the panel slides out of (templates/calendar/
 * _unscheduled_panel.html), so it is handled like one: click it, or grab it and
 * pull it toward the middle of the screen, and the panel opens; push it back,
 * or click again, and the panel closes. Which way is "out" follows the CSS
 * breakpoint that turns the panel into a bottom sheet (static/css/calendar.css).
 *
 * The pull is a threshold, not a live follow: past PULL_PX the panel runs its
 * own Bootstrap transition, and the tab rides along through the page's
 * body.unscheduled-open class. Each page still owns what opening means for it
 * (the calendar resizes FullCalendar, the board remembers the panel across
 * reloads) through the offcanvas events, so this controller only opens and
 * closes.
 */
import { Controller } from '@hotwired/stimulus';

// Far enough to be deliberate, short enough to feel like a flick.
const PULL_PX = 24;
// Must match the bottom-sheet breakpoint in static/css/calendar.css.
const SHEET_MEDIA = '(max-width: 768px)';

export default class extends Controller {
    static values = { panel: String };

    grab(event) {
        this._origin = { x: event.clientX, y: event.clientY };
        this._pulled = false;
        // Keep receiving moves once the pointer leaves the narrow tab.
        this.element.setPointerCapture(event.pointerId);
    }

    pull(event) {
        if (!this._origin || this._pulled) return;
        const outward = window.matchMedia(SHEET_MEDIA).matches
            ? this._origin.y - event.clientY
            : this._origin.x - event.clientX;
        const distance = this._panelEl().classList.contains('show') ? -outward : outward;
        if (distance < PULL_PX) return;
        this._pulled = true;
        this._offcanvas().toggle();
    }

    release() {
        this._origin = null;
    }

    toggle() {
        // A pull ends in a click on the same element; it has already toggled.
        if (this._pulled) {
            this._pulled = false;
            return;
        }
        this._offcanvas().toggle();
    }

    _panelEl() {
        return document.querySelector(this.panelValue);
    }

    _offcanvas() {
        return window.bootstrap.Offcanvas.getOrCreateInstance(this._panelEl());
    }
}
